import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Stack, Typography } from '@mui/material';
import GreenRow from './greenRow';
import { updateGreenSetup } from '../../features/event/eventSlice';

const GreenSetup = forwardRef(({ event }, ref) => {
  const [locations, setLocations] = React.useState([]);

  const dispatch = useDispatch();

  const getGreensPerLocation = React.useCallback(() => {
    // First, check if event.greens exists and has data - use it for existing configurations
    if (event && event.greens && Array.isArray(event.greens) && event.greens.length > 0) {
      return event.greens.map((location) => ({
        name: location.name,
        id: location.id,
        greens: Array.isArray(location.greens)
          ? location.greens.map((green) => ({
              name: green.name,
              rinks: Array.isArray(green.rinks) ? green.rinks : []
            }))
          : []
      }));
    }

    // Fallback to venue/locations data for initial setup
    if (event && event.venue && event.venue.id === 'multiple' && Array.isArray(event.locations)) {
      // [{ locationName, greens: [{ name, rinks }] }, ...]
      return event.locations.map((location) => ({
        name: location.name,
        id: location.id,
        greens: Array.isArray(location.greens)
          ? location.greens.map((green) => {
              const rinks = [];
              for (let j = 1; j <= green.rink_count; j++) {
                rinks.push({ rink: j, active: true });
              }
              return { name: green.name, rinks };
            })
          : []
      }));
    }

    // Single venue fallback
    if (event && event.venue && Array.isArray(event.venue.greens)) {
      return [
        {
          name: event.venue.name,
          id: event.venue.id,
          greens: event.venue.greens.map((green) => {
            const rinks = [];
            // Venue greens may be stored flat ({ rink_count }) or rink-level
            // ({ rinks }), so read either shape.
            const rinkCount =
              green.rink_count || (Array.isArray(green.rinks) ? green.rinks.length : 6);
            for (let j = 1; j <= rinkCount; j++) {
              rinks.push({ rink: j, active: true });
            }
            return { name: green.name, rinks };
          })
        }
      ];
    }

    return [];
  }, [event]);

  useEffect(() => {
    if (event) {
      setLocations(getGreensPerLocation());
    }
  }, [event, getGreensPerLocation]);

  const handleRinkToggle = (locationId, greenName, rinkNumber) => {
    setLocations((prevLocations) =>
      prevLocations.map((location) =>
        location.id === locationId
          ? {
              ...location,
              greens: location.greens.map((green) =>
                green.name === greenName
                  ? {
                      ...green,
                      rinks: green.rinks.map((rink) =>
                        rink.rink === rinkNumber ? { ...rink, active: !rink.active } : rink
                      )
                    }
                  : green
              )
            }
          : location
      )
    );
  };

  // Exposes an imperative `save()` so the parent wizard can persist the rink
  // selection whenever the user clicks Save Draft / Save & Close / Next,
  // instead of requiring a separate save action inside this panel.
  useImperativeHandle(
    ref,
    () => ({
      save: async () => {
        if (!event?.id) {
          return;
        }

        await dispatch(updateGreenSetup({ eventId: event.id, locations }));
      }
    }),
    [dispatch, event, locations]
  );

  if (!event) {
    return (
      <Typography variant="body2" color="text.secondary">
        Save the event details first, then come back here to configure the green setup.
      </Typography>
    );
  }

  return (
    <Stack gap={2}>
      {locations.map((location, index) => (
        <Box key={location.id}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            {index + 1}. {location.name}
          </Typography>
          {location.greens.map((green, greenIndex) => (
            <GreenRow
              key={`${location.id}-${green.name}-${greenIndex}`}
              venue={location}
              green={green}
              onRinkToggle={(greenName, rinkNumber) =>
                handleRinkToggle(location.id, greenName, rinkNumber)
              }
            />
          ))}
        </Box>
      ))}
    </Stack>
  );
});

GreenSetup.displayName = 'GreenSetup';

export default GreenSetup;
